import { Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { useEffect } from 'react';
import { motion, AnimatePresence, useIsPresent } from 'framer-motion';
import Home from './features/home/pages/home';
import Navbar from './components/navbar';
import BuscarPartido from './features/buscarpartido/pages/buscarpartido';
import Partido from './features/partido/pages/partido';
import Jugador from './features/jugador/pages/jugador';
import Jugadores from './features/jugadores/pages/jugadores';
import Tecnicos from './features/tecnicos/pages/tecnicos';
import Comparar from './features/comparar/pages/comparar';
import Planteles from './features/planteles/pages/planteles';
import Records from './features/records/pages/records';
import Presidentes from './features/presidentes/pages/presidentes';
import Fundacion from './features/fundacion/pages/fundacion';
import Canchas from './features/canchas/pages/canchas';
import Estrellas from './features/estrellas/pages/estrellas';
import Historial from './features/historial/pages/historial';
import Camisetas from './features/camisetas/pages/camisetas';
import Playoffs from './features/playoffs/pages/playoffs';
import NotFound from './features/notfound/pages/notfound';

const pageVariants = {
  initial: { x: '100%' },
  animate: { x: '0%' },
  exit: { x: '-100%' },
};

const pageTransition = { duration: 0.3, ease: 'easeInOut' };

if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const SCROLL_POSITIONS_KEY = 'app-scroll-positions';

function readScrollPositions() {
  try {
    return JSON.parse(sessionStorage.getItem(SCROLL_POSITIONS_KEY)) || {};
  } catch {
    return {};
  }
}

function saveScrollPosition(key, y) {
  try {
    const positions = readScrollPositions();
    positions[key] = y;
    sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(positions));
  } catch {
    // ignore storage errors (e.g. private browsing quota)
  }
}

function PageTransition({ children }) {
  const isPresent = useIsPresent();
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      style={{
        position: isPresent ? 'relative' : 'absolute',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
      }}
    >
      {children}
    </motion.div>
  );
}

function App() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const onScroll = () => saveScrollPosition(location.key, window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.key]);

  useEffect(() => {
    if (navigationType !== 'POP') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }

    const target = readScrollPositions()[location.key];
    if (target == null) {
      return;
    }

    // Content (data, images) may still be loading, so keep nudging the scroll
    // position until the page has grown tall enough to reach it, or give up.
    let attempts = 0;
    let frameId;
    const tryRestore = () => {
      attempts += 1;
      window.scrollTo({ top: target, left: 0, behavior: 'auto' });
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll < target && attempts < 30) {
        frameId = requestAnimationFrame(tryRestore);
      }
    };
    frameId = requestAnimationFrame(tryRestore);

    return () => cancelAnimationFrame(frameId);
  }, [location.pathname, location.key, navigationType]);

  const routes = [
    { path: '/', element: <Home /> },
    { path: 'partidos/buscarpartido', element: <BuscarPartido /> },
    { path: 'perfiles/jugadores', element: <Jugadores /> },
    { path: 'perfiles/tecnicos', element: <Tecnicos /> },
    { path: 'partido/:partidoId', element: <Partido /> },
    { path: 'jugador/:nombreJugador', element: <Jugador /> },
    { path: 'perfiles/comparar', element: <Comparar /> },
    { path: 'perfiles/planteles', element: <Planteles /> },
    { path: 'historia/fundacion', element: <Fundacion /> },
    { path: 'historia/canchas', element: <Canchas /> },
    { path: 'historia/estrellas', element: <Estrellas /> },
    { path: 'historia/presidentes', element: <Presidentes /> },
    { path: 'historia/camisetas', element: <Camisetas /> },
    { path: 'historial', element: <Historial /> },
    { path: 'partidos/playoffs', element: <Playoffs /> },
    { path: 'records', element: <Records /> },
  ];

  return (
    <>
      <Navbar />
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <AnimatePresence mode="sync" initial={false}>
          <Routes location={location} key={location.pathname}>
            {routes.map(({ path, element }) => (
              <Route
                key={path}
                path={path}
                element={<PageTransition>{element}</PageTransition>}
              />
            ))}
            <Route path='*' element={<NotFound />} />
          </Routes>
        </AnimatePresence>
      </div>
    </>
  );
}

export default App;
